import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Row, Col, Tabs, Tab } from "react-bootstrap";
import Select from "react-select";
import { Plus, Trash2 } from "lucide-react";
import {
  MovingAverage,
  LogicCondition,
  RiskParameters,
  ScheduleParameters,
  LinkedAlgo,
  TradingInstance,
  MAType,
  ShutdownDay,
  RestartDay,
  AssetClass,
  Symbol,
} from "../../types";
import { MultiValue } from "react-select";

interface AlgoEditModalProps {
  show: boolean;
  onHide: () => void;
  algo: LinkedAlgo;
  instance: TradingInstance;
  onSave: (algoId: number, params: any) => void;
}

interface SelectOption {
  value: number;
  label: string;
}

const AlgoEditModal: React.FC<AlgoEditModalProps> = ({
  show,
  onHide,
  algo,
  instance,
  onSave,
}) => {
  // Initialize states with existing parameters
  const [name, setName] = useState(algo.name);
  const [description, setDescription] = useState(algo.description || "");
  const [movingAverages, setMovingAverages] = useState<MovingAverage[]>(
    algo.parameters?.movingAverages || []
  );
  const [buyConditions, setBuyConditions] = useState<LogicCondition[]>(
    algo.parameters?.entryConditions?.buyConditions || []
  );
  const [sellConditions, setSellConditions] = useState<LogicCondition[]>(
    algo.parameters?.entryConditions?.sellConditions || []
  );
  const [buyExitConditions, setBuyExitConditions] = useState<LogicCondition[]>(
    algo.parameters?.exitConditions?.buyExitConditions || []
  );
  const [sellExitConditions, setSellExitConditions] = useState<
    LogicCondition[]
  >(algo.parameters?.exitConditions?.sellExitConditions || []);
  const [riskParams, setRiskParams] = useState<RiskParameters>(
    algo.parameters?.riskParameters || {}
  );
  const [scheduleParams, setScheduleParams] = useState<ScheduleParameters>(
    algo.parameters?.scheduleParameters || {}
  );

  const [selectedAssetClasses, setSelectedAssetClasses] = useState<
    SelectOption[]
  >(
    ((algo.asset_classes as AssetClass[]) || []).map((ac: AssetClass) => ({
      value: ac.id,
      label: ac.name,
    }))
  );

  const [selectedSymbols, setSelectedSymbols] = useState<SelectOption[]>(
    ((algo.symbols as Symbol[]) || []).map((s: Symbol) => ({
      value: s.id,
      label: s.name,
    }))
  );

  // Update all states when algo changes
  useEffect(() => {
    if (algo) {
      // Basic fields
      setName(algo.name);
      setDescription(algo.description || "");

      // Parameters
      if (algo.parameters) {
        setMovingAverages(algo.parameters.movingAverages || []);
        setBuyConditions(algo.parameters.entryConditions?.buyConditions || []);
        setSellConditions(
          algo.parameters.entryConditions?.sellConditions || []
        );
        setBuyExitConditions(
          algo.parameters.exitConditions?.buyExitConditions || []
        );
        setSellExitConditions(
          algo.parameters.exitConditions?.sellExitConditions || []
        );
        setRiskParams(algo.parameters.riskParameters || {});
        setScheduleParams(algo.parameters.scheduleParameters || {});
      }

      // Asset Classes and Symbols
      console.log("Setting asset classes:", algo.asset_classes);
      console.log("Setting symbols:", algo.symbols);

      setSelectedAssetClasses(
        (algo.asset_classes || []).map((ac) => ({
          value: ac.id,
          label: ac.name,
        }))
      );

      setSelectedSymbols(
        (algo.symbols || []).map((s) => ({
          value: s.id,
          label: s.name,
        }))
      );
    }
  }, [algo]);

  // Helper function for condition rows - same as AddAlgoModal
  const renderConditionRow = (
    condition: LogicCondition,
    index: number,
    conditions: LogicCondition[],
    setConditions: React.Dispatch<React.SetStateAction<LogicCondition[]>>
  ) => (
    <Row key={index} className="mb-3 align-items-center">
      <Col>
        <Form.Select
          value={`${condition.indicator1.type}_${condition.indicator1.period}`}
          onChange={(e) => {
            const newConditions = [...conditions];
            const [type, period] = e.target.value.split("_");
            const selectedMA = movingAverages.find(
              (ma) => ma.type === type && ma.period === parseInt(period)
            );
            if (selectedMA) {
              newConditions[index] = {
                ...condition,
                indicator1: selectedMA,
              };
              setConditions(newConditions);
            }
          }}
        >
          {movingAverages.map((ma) => (
            <option
              key={`${ma.type}_${ma.period}`}
              value={`${ma.type}_${ma.period}`}
            >
              {ma.type} {ma.period}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col>
        <Form.Select
          value={condition.relationship}
          onChange={(e) => {
            const newConditions = [...conditions];
            newConditions[index] = {
              ...condition,
              relationship: e.target.value as
                | "Crosses above"
                | "Crosses below"
                | "Is above"
                | "Is below",
            };
            setConditions(newConditions);
          }}
        >
          <option value="Crosses above">Crosses above</option>
          <option value="Crosses below">Crosses below</option>
          <option value="Is above">Is above</option>
          <option value="Is below">Is below</option>
        </Form.Select>
      </Col>
      <Col>
        <Form.Select
          value={`${condition.indicator2.type}_${condition.indicator2.period}`}
          onChange={(e) => {
            const newConditions = [...conditions];
            const [type, period] = e.target.value.split("_");
            const selectedMA = movingAverages.find(
              (ma) => ma.type === type && ma.period === parseInt(period)
            );
            if (selectedMA) {
              newConditions[index] = {
                ...condition,
                indicator2: selectedMA,
              };
              setConditions(newConditions);
            }
          }}
        >
          {movingAverages.map((ma) => (
            <option
              key={`${ma.type}_${ma.period}`}
              value={`${ma.type}_${ma.period}`}
            >
              {ma.type} {ma.period}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs="auto">
        <Button
          variant="danger"
          onClick={() =>
            setConditions(conditions.filter((_, i) => i !== index))
          }
        >
          <Trash2 size={16} />
        </Button>
      </Col>
    </Row>
  );

  // Save handler with strategy type check
  const handleSave = () => {
    const params = {
      name,
      description,
      parameters: {
        strategy_type: algo.parameters?.strategy_type || "MA_CROSSOVER",
        movingAverages,
        entryConditions: {
          buyConditions,
          sellConditions,
        },
        exitConditions: {
          buyExitConditions,
          sellExitConditions,
        },
        riskParameters: riskParams,
        scheduleParameters: scheduleParams,
      },
      asset_classes: selectedAssetClasses.map((ac) => ({
        id: ac.value,
        name: ac.label,
      })),
      symbols: selectedSymbols.map((s) => ({ id: s.value, name: s.label })),
    };
    onSave(algo.id, params);
  };

  // Update onChange handlers
  const handleAssetClassChange = (selected: MultiValue<SelectOption>) => {
    setSelectedAssetClasses([...selected]);
  };

  const handleSymbolChange = (selected: MultiValue<SelectOption>) => {
    setSelectedSymbols([...selected]);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Trading Strategy</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Asset Classes</Form.Label>
                <Select
                  isMulti
                  value={selectedAssetClasses}
                  onChange={handleAssetClassChange}
                  options={instance.asset_classes?.map((ac) => ({
                    value: ac.id,
                    label: ac.name,
                  }))}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Symbols</Form.Label>
                <Select
                  isMulti
                  value={selectedSymbols}
                  onChange={handleSymbolChange}
                  options={instance.symbols?.map((s) => ({
                    value: s.id,
                    label: s.name,
                  }))}
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Strategy Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Tabs defaultActiveKey="indicators">
            <Tab eventKey="indicators" title="Indicators">
              <div className="p-3">
                <h5>Moving Averages</h5>
                {movingAverages.map((ma, index) => (
                  <Row key={index} className="mb-3 align-items-center">
                    <Col>
                      <Form.Select
                        value={ma.type}
                        onChange={(e) => {
                          const newMAs = [...movingAverages];
                          newMAs[index].type = e.target.value as MAType;
                          setMovingAverages(newMAs);
                        }}
                      >
                        {Object.values(MAType).map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col>
                      <Form.Control
                        type="number"
                        value={ma.period}
                        onChange={(e) => {
                          const newMAs = [...movingAverages];
                          newMAs[index].period = parseInt(e.target.value);
                          setMovingAverages(newMAs);
                        }}
                      />
                    </Col>
                    <Col>
                      <Form.Select
                        value={ma.priceType}
                        onChange={(e) => {
                          const newMAs = [...movingAverages];
                          newMAs[index].priceType = e.target.value as any;
                          setMovingAverages(newMAs);
                        }}
                      >
                        <option value="close">Close</option>
                        <option value="open">Open</option>
                        <option value="high">High</option>
                        <option value="low">Low</option>
                        <option value="hl2">HL/2</option>
                        <option value="hlc3">HLC/3</option>
                        <option value="ohlc4">OHLC/4</option>
                      </Form.Select>
                    </Col>
                    <Col xs="auto">
                      <Button
                        variant="danger"
                        onClick={() => {
                          setMovingAverages(
                            movingAverages.filter((_, i) => i !== index)
                          );
                        }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  variant="primary"
                  onClick={() => {
                    setMovingAverages([
                      ...movingAverages,
                      {
                        type: MAType.SMA,
                        period: 21,
                        priceType: "close",
                        shift: 0,
                      },
                    ]);
                  }}
                >
                  <Plus size={16} className="me-2" />
                  Add Moving Average
                </Button>
              </div>
            </Tab>

            <Tab eventKey="entry-logic" title="Entry Logic">
              <div className="p-3">
                <h5>Buy Conditions</h5>
                {buyConditions.map((condition, index) =>
                  renderConditionRow(
                    condition,
                    index,
                    buyConditions,
                    setBuyConditions
                  )
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    if (movingAverages.length >= 2) {
                      setBuyConditions([
                        ...buyConditions,
                        {
                          indicator1: movingAverages[0],
                          relationship: "Crosses above",
                          indicator2: movingAverages[1],
                        },
                      ]);
                    }
                  }}
                >
                  <Plus size={16} className="me-2" />
                  Add Buy Condition
                </Button>

                <h5 className="mt-4">Sell Conditions</h5>
                {sellConditions.map((condition, index) =>
                  renderConditionRow(
                    condition,
                    index,
                    sellConditions,
                    setSellConditions
                  )
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    if (movingAverages.length >= 2) {
                      setSellConditions([
                        ...sellConditions,
                        {
                          indicator1: movingAverages[0],
                          relationship: "Crosses below",
                          indicator2: movingAverages[1],
                        },
                      ]);
                    }
                  }}
                >
                  <Plus size={16} className="me-2" />
                  Add Sell Condition
                </Button>
              </div>
            </Tab>

            <Tab eventKey="exit-logic" title="Exit Logic">
              <div className="p-3">
                <h5>Buy Exit Conditions</h5>
                {buyExitConditions.map((condition, index) =>
                  renderConditionRow(
                    condition,
                    index,
                    buyExitConditions,
                    setBuyExitConditions
                  )
                )}
                <Button
                  variant="primary"
                  className="mb-4"
                  onClick={() => {
                    setBuyExitConditions([
                      ...buyExitConditions,
                      {
                        indicator1: movingAverages[0],
                        relationship: "Crosses below",
                        indicator2: movingAverages[1],
                      },
                    ]);
                  }}
                >
                  <Plus size={16} className="me-2" />
                  Add Buy Exit Condition
                </Button>

                <h5>Sell Exit Conditions</h5>
                {sellExitConditions.map((condition, index) =>
                  renderConditionRow(
                    condition,
                    index,
                    sellExitConditions,
                    setSellExitConditions
                  )
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    setSellExitConditions([
                      ...sellExitConditions,
                      {
                        indicator1: movingAverages[0],
                        relationship: "Crosses above",
                        indicator2: movingAverages[1],
                      },
                    ]);
                  }}
                >
                  <Plus size={16} className="me-2" />
                  Add Sell Exit Condition
                </Button>
              </div>
            </Tab>

            <Tab eventKey="risk-management" title="Risk Management">
              <div className="p-3">
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Lot Size Per Symbol</Form.Label>
                      <Form.Control
                        value={riskParams.lotSizePerSymbol}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            lotSizePerSymbol: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Risk Source</Form.Label>
                      <Form.Select
                        value={riskParams.riskSource}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            riskSource: e.target.value as
                              | "FreeMargin"
                              | "Balance"
                              | "Equity",
                          })
                        }
                      >
                        <option value="FreeMargin">Free Margin</option>
                        <option value="Balance">Balance</option>
                        <option value="Equity">Equity</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Risk Percentage</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        value={riskParams.riskPercentage}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            riskPercentage: parseFloat(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Max Drawdown % (Portfolio)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        value={riskParams.maxDrawdownPercentage}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            maxDrawdownPercentage: parseFloat(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Close Trade After Drawdown ($)</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={riskParams.closeTradeAfterDrawdown || 0}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            closeTradeAfterDrawdown: parseFloat(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="addSpreadToSL"
                      label="Add Spread to SL"
                      checked={riskParams.addSpreadToSL}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          addSpreadToSL: e.target.checked,
                        })
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="addSpreadToTP"
                      label="Add Spread to TP"
                      checked={riskParams.addSpreadToTP}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          addSpreadToTP: e.target.checked,
                        })
                      }
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>TP per Symbol</Form.Label>
                      <Form.Control
                        type="text"
                        value={riskParams.tpPerSymbol}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            tpPerSymbol: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>SL per Symbol</Form.Label>
                      <Form.Control
                        type="text"
                        value={riskParams.slPerSymbol}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            slPerSymbol: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Max Risk Per Symbol Type</Form.Label>
                      <Form.Select
                        value={riskParams.maxRiskPerSymbolType}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            maxRiskPerSymbolType: e.target.value as
                              | "Fixed"
                              | "Percentage",
                          })
                        }
                      >
                        <option value="Fixed">Fixed ($)</option>
                        <option value="Percentage">Percentage (%)</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>
                        Max Risk Per Symbol (
                        {riskParams.maxRiskPerSymbolType === "Fixed"
                          ? "$"
                          : "%"}
                        )
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="EURUSD=1000,GBPUSD=500"
                        value={riskParams.maxRiskPerSymbol}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            maxRiskPerSymbol: e.target.value,
                          })
                        }
                      />
                      <Form.Text className="text-muted">
                        Format: SYMBOL=VALUE, SYMBOL=VALUE (e.g.,
                        EURUSD=1000,GBPUSD=500)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Tab>

            <Tab eventKey="execution" title="Execution">
              <div className="p-3">
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Order Type</Form.Label>
                      <Form.Select
                        value={riskParams.orderType}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            orderType: e.target.value as
                              | "Market"
                              | "Limit"
                              | "Stop"
                              | "Stop Limit",
                          })
                        }
                      >
                        <option value="Market">Market</option>
                        <option value="Limit">Limit</option>
                        <option value="Stop">Stop</option>
                        <option value="Stop Limit">Stop Limit</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Max Order Count</Form.Label>
                      <Form.Control
                        type="number"
                        value={riskParams.maxOrderCount}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            maxOrderCount: parseInt(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="allowPyramiding"
                      label="Allow Pyramiding"
                      checked={riskParams.allowPyramiding}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          allowPyramiding: e.target.checked,
                        })
                      }
                    />
                    {riskParams.allowPyramiding && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Pyramid Amount</Form.Label>
                          <Form.Control
                            type="number"
                            value={riskParams.maxPyramidPositions}
                            onChange={(e) =>
                              setRiskParams({
                                ...riskParams,
                                maxPyramidPositions: parseInt(e.target.value),
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Col>
                </Row>
              </div>
            </Tab>

            <Tab eventKey="time-management" title="Time">
              <div className="p-3">
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Daily Start Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={riskParams.dailyStartTime}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            dailyStartTime: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Daily Stop Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={riskParams.dailyStopTime}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            dailyStopTime: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Check
                  type="switch"
                  id="useShutdown"
                  label="Use Weekly Shutdown"
                  checked={riskParams.useShutdown}
                  onChange={(e) =>
                    setRiskParams({
                      ...riskParams,
                      useShutdown: e.target.checked,
                    })
                  }
                  className="mb-3"
                />

                {riskParams.useShutdown && (
                  <>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Close Day</Form.Label>
                          <Form.Select
                            value={riskParams.closeDay}
                            onChange={(e) =>
                              setRiskParams({
                                ...riskParams,
                                closeDay: e.target.value as ShutdownDay,
                              })
                            }
                          >
                            {Object.values(ShutdownDay).map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Close Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={riskParams.closeTime}
                            onChange={(e) =>
                              setRiskParams({
                                ...riskParams,
                                closeTime: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Restart Day</Form.Label>
                          <Form.Select
                            value={riskParams.restartDay}
                            onChange={(e) =>
                              setRiskParams({
                                ...riskParams,
                                restartDay: e.target.value as RestartDay,
                              })
                            }
                          >
                            {Object.values(RestartDay).map((day) => (
                              <option key={day} value={day}>
                                {day}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Restart Time</Form.Label>
                          <Form.Control
                            type="time"
                            value={riskParams.restartTime}
                            onChange={(e) =>
                              setRiskParams({
                                ...riskParams,
                                restartTime: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            </Tab>

            <Tab eventKey="schedule" title="Schedule">
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="activeShutdown"
                  label="Enable Schedule"
                  checked={scheduleParams.activeShutdown}
                  onChange={(e) =>
                    setScheduleParams({
                      ...scheduleParams,
                      activeShutdown: e.target.checked,
                    })
                  }
                />
              </Form.Group>

              {scheduleParams.activeShutdown && (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Daily Start Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduleParams.dailyStartTime}
                          onChange={(e) =>
                            setScheduleParams({
                              ...scheduleParams,
                              dailyStartTime: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Daily Stop Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduleParams.dailyStopTime}
                          onChange={(e) =>
                            setScheduleParams({
                              ...scheduleParams,
                              dailyStopTime: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Weekly Close Day</Form.Label>
                        <Form.Select
                          value={scheduleParams.closeDay}
                          onChange={(e) =>
                            setScheduleParams({
                              ...scheduleParams,
                              closeDay: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Day</option>
                          {[
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday",
                          ].map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Close Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduleParams.closeTime}
                          onChange={(e) =>
                            setScheduleParams({
                              ...scheduleParams,
                              closeTime: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Weekly Restart Day</Form.Label>
                        <Form.Select
                          value={scheduleParams.restartDay}
                          onChange={(e) =>
                            setScheduleParams({
                              ...scheduleParams,
                              restartDay: e.target.value,
                            })
                          }
                        >
                          <option value="">Select Day</option>
                          {[
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday",
                          ].map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Restart Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={scheduleParams.restartTime}
                          onChange={(e) =>
                            setScheduleParams({
                              ...scheduleParams,
                              restartTime: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            </Tab>

            <Tab eventKey="trade-management" title="Trade Management">
              <div className="p-3">
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Breakeven After Profit</Form.Label>
                      <Form.Control
                        type="number"
                        value={riskParams.breakevenAfterProfit}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            breakevenAfterProfit: parseFloat(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Close Trade After Drawdown</Form.Label>
                      <Form.Control
                        type="number"
                        value={riskParams.closeTradeAfterDrawdown}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            closeTradeAfterDrawdown: parseFloat(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="useTrailingStop"
                      label="Use Trailing Stop"
                      checked={riskParams.useTrailingStop}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          useTrailingStop: e.target.checked,
                        })
                      }
                    />
                    {riskParams.useTrailingStop && (
                      <Form.Control
                        type="number"
                        value={riskParams.trailingStopPoints}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            trailingStopPoints: parseInt(e.target.value),
                          })
                        }
                      />
                    )}
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="useTrailingTakeProfit"
                      label="Use Trailing Take Profit"
                      checked={riskParams.useTrailingTakeProfit}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          useTrailingTakeProfit: e.target.checked,
                        })
                      }
                    />
                    {riskParams.useTrailingTakeProfit && (
                      <Form.Control
                        type="number"
                        value={riskParams.trailingTakeProfitPoints}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            trailingTakeProfitPoints: parseInt(e.target.value),
                          })
                        }
                      />
                    )}
                  </Col>
                </Row>
              </div>
            </Tab>

            <Tab eventKey="order-settings" title="Order Settings">
              <div className="p-3">
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Order Type</Form.Label>
                      <Form.Select
                        value={riskParams.orderType}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            orderType: e.target.value as
                              | "Market"
                              | "Limit"
                              | "Stop"
                              | "Stop Limit",
                          })
                        }
                      >
                        <option value="Market">Market</option>
                        <option value="Limit">Limit</option>
                        <option value="Stop">Stop</option>
                        <option value="Stop Limit">Stop Limit</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Max Order Count</Form.Label>
                      <Form.Control
                        type="number"
                        value={riskParams.maxOrderCount}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            maxOrderCount: parseInt(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="executionReversal"
                      label="Execution Reversal"
                      checked={riskParams.executionReversal}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          executionReversal: e.target.checked,
                        })
                      }
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="switch"
                      id="allowPyramiding"
                      label="Allow Pyramiding"
                      checked={riskParams.allowPyramiding}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          allowPyramiding: e.target.checked,
                        })
                      }
                    />
                  </Col>
                </Row>

                {riskParams.allowPyramiding && (
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Pyramid Amount</Form.Label>
                        <Form.Control
                          type="number"
                          value={riskParams.maxPyramidPositions}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              maxPyramidPositions: parseInt(e.target.value),
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Pyramid Size</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.1"
                          value={riskParams.pyramidSize}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              pyramidSize: parseFloat(e.target.value),
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </div>
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AlgoEditModal;
